import { useCallback, useEffect, useState } from 'react';
import type { EvaluationDraft } from '../types';
import { DEFAULT_CONFIG, MAX_TASK_NAME } from '../constants';

const KEY = 'article-evaluation:draft';
const EMPTY: EvaluationDraft = {
  mode: 'single',
  task_name: '',
  keywords_text: '',
  article_a: { title: '', body: '' },
  article_b: { title: '', body: '' }
};
export type DraftErrors = Partial<
  Record<
    | 'task_name'
    | 'keywords_text'
    | 'article_a_title'
    | 'article_a_body'
    | 'article_b_title'
    | 'article_b_body',
    string
  >
>;
export function normalizeKeywords(value: string) {
  return [
    ...new Set(
      value
        .split(/[,，;；\n]+/)
        .map((item) => item.trim())
        .filter(Boolean)
    )
  ].join(', ');
}

export function useEvaluationDraft(limits = DEFAULT_CONFIG) {
  const [draft, setDraft] = useState<EvaluationDraft>(() => {
    try {
      return { ...EMPTY, ...JSON.parse(sessionStorage.getItem(KEY) || '{}') };
    } catch {
      return EMPTY;
    }
  });
  const [errors, setErrors] = useState<DraftErrors>({});
  useEffect(() => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(draft));
    } catch {
      /* session-only best effort */
    }
  }, [draft]);
  const validate = useCallback(() => {
    const next: DraftErrors = {};
    if (!draft.task_name.trim()) next.task_name = '请填写任务名';
    else if (draft.task_name.length > MAX_TASK_NAME)
      next.task_name = `任务名不能超过 ${MAX_TASK_NAME} 个字符`;
    const normalized = normalizeKeywords(draft.keywords_text);
    if (!normalized) next.keywords_text = '请填写核心关键词';
    else if (normalized.length > limits.keywords_max_chars)
      next.keywords_text = `关键词不能超过 ${limits.keywords_max_chars} 个字符`;
    if (!draft.article_a.title.trim()) next.article_a_title = '请填写文章 A 标题';
    if (!draft.article_a.body.trim()) next.article_a_body = '请填写文章 A 正文';
    else if (draft.article_a.body.length > limits.article_max_chars)
      next.article_a_body = `正文不能超过 ${limits.article_max_chars} 个字符`;
    if (draft.mode === 'compare') {
      if (!draft.article_b.title.trim()) next.article_b_title = '双篇对比需要填写文章 B 标题';
      if (!draft.article_b.body.trim()) next.article_b_body = '双篇对比需要填写文章 B 正文';
      else if (draft.article_b.body.length > limits.article_max_chars)
        next.article_b_body = `正文不能超过 ${limits.article_max_chars} 个字符`;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [draft, limits]);
  const clear = useCallback(() => {
    setDraft(EMPTY);
    setErrors({});
    try {
      sessionStorage.removeItem(KEY);
    } catch {}
  }, []);
  return {
    draft,
    setDraft,
    errors,
    validate,
    normalize: () =>
      setDraft((value) => ({ ...value, keywords_text: normalizeKeywords(value.keywords_text) })),
    clear
  };
}
